import { useEffect, useMemo, useReducer, useState } from "react";
import * as Icon from "react-feather";
import APIManager from "../../../../managers/APIManager";
import TagsInput from "../../../common/TagsInput";
import { capitalizeFirst, upperCase } from "../../../../utils/manipulateText";
import { useTranslation, Translation } from "react-i18next";
import { useSelector } from "react-redux";
import { modalConstants } from "../constants/modalConstants";
import MultiselectBox from "../../../common/MultiselectBox";
import LoadingBackDrop from "../../../common/LoadingBackDrop";
import {
  actionTypes,
  reducer,
  initialState as _initialState,
} from "./actionTypes";
import { capitalize } from "lodash";
import MultiSelectMenu from "../../../common/MultiSelectMenu";

const allSitesObj = {
  id: "all-sites",
  name: "All Sites",
  fqdn: "All Sites",
  virgulId: "all-sites",
};

const ConfigModal = ({
  isVisible,
  operation = "create",
  onClose,
  onRefreshData,
}) => {
  const selectedSite = useSelector((state) => state.site.selectedSite);
  const sites = useSelector((state) => [allSitesObj, ...state.site.sites]);
  const userVirgulId = useSelector((state) => state?.auth?.user?.virgulId);
  const userEmail = useSelector((state) => state?.auth?.user?.email);

  const initialState = useMemo(
    () => ({
      ..._initialState,
      emails: [userEmail],
      selectedSite,
    }),
    [userEmail]
  );

  const [state, dispatch] = useReducer(reducer, initialState);

  const [loading, setLoading] = useState(false);

  const [isEmailEmpty, setIsEmailEmpty] = useState(false);
  const [isReportNameEmpty, setIsReportNameEmpty] = useState(false);
  const { t } = useTranslation();

  const [selectedIncomeTypes, setSelectedIncomeTypes] = useState([]);

  useEffect(() => {
    if (!isVisible) return;
    dispatch({ type: actionTypes.REPLACE_ALL, payload: { ...initialState } });

    if (!state.selectedSite?.id) return;

    const siteBdAll = Object.keys(modalConstants.siteBreakdownsAll);
    const filteredBd = selectedSite?.breakdowns?.filter(
      (bd) => siteBdAll.indexOf(bd) !== -1
    );
    filteredBd.push("site");
    dispatch({
      type: actionTypes.SET_SITE_BREAKDOWNS,
      payload: filteredBd,
    });

    const platformsAll = Object.keys(modalConstants.sitePlatformsAll);

    var filteredPlatforms = selectedSite?.filters.filter(
      (platform) => platformsAll.indexOf(platform) !== -1
    );

    dispatch({
      type: actionTypes.SET_SITE_PLATFORMS,
      payload: ["all", ...filteredPlatforms],
    });

    setSelectedIncomeTypes(state.selectedIncomeTypes || []);
  }, [isVisible]);

  if (!isVisible) return null;

  const handleAdd = () => {
    var reportParams = {
      userId: userVirgulId,
      reportStatus: state.status,
      recipients: state.emails.join(";"),
      reportName: state.reportName,
      period: state.reportPeriod,
      periodDay:
        state.reportPeriod === "weekly"
          ? state.weekDay
          : state.reportPeriod === "monthly"
          ? state.monthDay
          : -1,
      periodHour: state.selectedHour,
      timezone: state.timezone,
      reportDateRange: state.dateRange,
      site: state.selectedSite,
    };

    var reportFilter = {};

    if (reportParams.site === "") reportParams.site = "all-sites";
    reportParams.reportType = "site";
    reportFilter.platform = state.selectedPlatforms[0];
    reportFilter.appNames = state.selectedBreakdowns.indexOf("appNames") !== -1;
    reportFilter.day = state.selectedBreakdowns.indexOf("daily") !== -1;
    reportFilter.month = state.selectedBreakdowns.indexOf("monthly") !== -1;
    reportFilter.site = state.selectedBreakdowns.indexOf("site") !== -1;
    reportFilter.tag = false;
    reportFilter.tagContent = state.selectedBreakdowns.indexOf("tag") !== -1;
    reportFilter.zone = state.selectedBreakdowns.indexOf("placement") !== -1;
    reportFilter.platformTag =
      state.selectedBreakdowns.indexOf("platform") !== -1;
    reportFilter.zoneIds = state.selectedPlacements;
    reportFilter.incomeTypes = selectedIncomeTypes;
    reportParams.site = sites.find(
      (s) => s.id === state.selectedSite?.id
    ).virgulId;

    reportParams.reportFilter = reportFilter;

    if (!state.reportName || !state?.emails?.length) {
      if (!state.reportName) {
        setIsReportNameEmpty(true);
      }

      if (!state?.emails?.length) {
        setIsEmailEmpty(true);
      }
      return;
    }

    setLoading(true);
    if (operation === "create") {
      APIManager.createScheduledReport(reportParams).then(() => {
        setLoading(false);
        if (onRefreshData) onRefreshData();
        onClose();
      });
    } else if (operation === "update") {
      APIManager.updateScheduledReport(state.id, reportParams).then(() => {
        setLoading(false);
        if (onRefreshData) onRefreshData();
        onClose();
      });
    } else {
      APIManager.createScheduledReport(reportParams).then(() => {
        setLoading(false);
        window.location = "/reports";
      });
    }
  };

  const onBreakdownChange = (e) => {
    e.preventDefault();
    var currDataSet = e.target.parentNode.dataset.breakdown;
    var parentDataSet = e.target.dataset.breakdown;
    var breakdownElement = null;
    if (currDataSet) breakdownElement = currDataSet;
    if (parentDataSet) breakdownElement = parentDataSet;

    if (breakdownElement === undefined) return;

    var bdList = [...state.selectedBreakdowns];
    if (bdList.indexOf(breakdownElement) === -1) {
      bdList.push(breakdownElement);
    } else {
      bdList.splice(bdList.indexOf(breakdownElement), 1);
    }
    dispatch({
      type: actionTypes.SET_SELECTED_BREAKDOWNS,
      payload: [...bdList],
    });
  };

  const onPlatformChange = (e) => {
    e.preventDefault();
    dispatch({
      type: actionTypes.SET_SELECTED_PLATFORMS,
      payload: [e.target.dataset.platform],
    });
  };

  const onAdPlacementChange = (placements) => {
    dispatch({
      type: actionTypes.SET_SELECTED_PLACEMENTS,
      payload: placements,
    });
  };

  return (
    <div
      id="wrapper"
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex justify-center items-center z-80 overflow-y:auto"
    >
      <div className="bg-white max-w-sm sm:max-w-lg md:max-w-5xl rounded-md w-full max-h-screen overflow-auto">
        {loading && <LoadingBackDrop />}
        <>
          <div className="p-3 flex items-center justify-between border-b border-b-gray-300">
            <h3 className="font-semibold text-m">
              {capitalize(t("Create New Report"))}
            </h3>
            <span className="cursor-pointer" onClick={onClose}>
              <Icon.X width={20} height={20} />
            </span>
          </div>
          <div className="mx-5 my-3">
            <div className="flex flex-wrap -mx-3">
              <div className="w-1/3 md:w-1/5 px-2 mb-3">
                <label
                  className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  htmlFor="reportStatus"
                >
                  <Translation>
                    {(t) => capitalizeFirst(t("report status"))}
                  </Translation>
                </label>
                <select
                  className="block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  id="reportStatus"
                  onChange={(e) =>
                    dispatch({
                      type: actionTypes.SET_STATUS,
                      payload: e.target.value,
                    })
                  }
                  defaultValue={initialState.status}
                >
                  <option value={1}>{capitalizeFirst(t("active"))}</option>
                  <option value={0}>{capitalizeFirst(t("inactive"))}</option>
                </select>
              </div>
              <div className="w-2/3 md:w-3/5 px-2 mb-3">
                <label
                  className={` ${
                    !isReportNameEmpty ? "text-gray-700" : "text-red-400"
                  } block uppercase tracking-wide  text-xs font-bold mb-2`}
                  htmlFor="reportName"
                >
                  <Translation>
                    {(t) => capitalizeFirst(t("report name"))}
                  </Translation>
                </label>
                <input
                  className={`appearance-none block w-full bg-gray-200 text-gray-700 border ${
                    !isReportNameEmpty ? "border-gray-200" : "border-red-400"
                  } rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500`}
                  id="reportName"
                  type="text"
                  placeholder={capitalize(t("enter report name"))}
                  onClick={() => {
                    setIsReportNameEmpty(false);
                  }}
                  onChange={(e) =>
                    dispatch({
                      type: actionTypes.SET_REPORT_NAME,
                      payload: e.target.value,
                    })
                  }
                  defaultValue={initialState.reportName}
                />
              </div>

              <div className="w-full md:w-1/5 px-2 mb-3">
                <label
                  className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  htmlFor="reportPeriod"
                >
                  <Translation>
                    {(t) => capitalizeFirst(t("report period"))}
                  </Translation>
                </label>
                <select
                  className=" block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  id="reportPeriod"
                  onChange={(e) =>
                    dispatch({
                      type: actionTypes.SET_REPORT_PERIOD,
                      payload: e.target.value,
                    })
                  }
                  defaultValue={initialState.reportPeriod}
                >
                  <option value="daily">{capitalizeFirst(t("daily"))} </option>
                  <option value="weekly">{capitalizeFirst(t("weekly"))}</option>
                  <option value="monthly">
                    {capitalizeFirst(t("monthly"))}
                  </option>
                </select>
              </div>
            </div>
            <div className="flex flex-wrap -mx-3 ">
              <div className="w-full md:w-1/4 px-2 mb-3">
                <label
                  className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  htmlFor="dateRange"
                >
                  <Translation>
                    {(t) => capitalizeFirst(t("date range"))}
                  </Translation>
                </label>
                <select
                  className=" block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  id="dateRange"
                  onChange={(e) =>
                    dispatch({
                      type: actionTypes.SET_DATE_RANGE,
                      payload: e.target.value,
                    })
                  }
                  defaultValue={initialState.dateRange}
                >
                  <option value="daily">
                    {capitalizeFirst(t("yesterday"))}
                  </option>
                  <option value="thisWeek">
                    {capitalizeFirst(t("this week"))}
                  </option>
                  <option value="thisMonth">
                    {capitalizeFirst(t("this month"))}
                  </option>
                  <option value="thisYear">
                    {capitalizeFirst(t("this year"))}
                  </option>
                  <option value="lastWeek">
                    {capitalizeFirst(t("last week"))}
                  </option>
                  <option value="weekly">
                    {capitalizeFirst(t("last 7 days"))}
                  </option>
                  <option value="monthly">
                    {capitalizeFirst(t("last 30 days"))}
                  </option>
                  <option value="lastMonth">
                    {capitalizeFirst(t("last month"))}
                  </option>
                  <option value="last3M">
                    {capitalizeFirst(t("last 3 months"))}
                  </option>
                  <option value="last6M">
                    {capitalizeFirst(t("last 6 months"))}
                  </option>
                  <option value="lastYear">
                    {capitalizeFirst(t("last year"))}
                  </option>
                </select>
              </div>

              {state.reportPeriod !== "daily" && (
                <div className="w-1/2 md:w-1/4 px-2 mb-3">
                  <label
                    className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    htmlFor="periodDay"
                  >
                    <Translation>
                      {(t) => capitalizeFirst(t("delivery day"))}
                    </Translation>
                  </label>
                  {state.reportPeriod === "monthly" && (
                    <select
                      className=" block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                      id="periodDay"
                      onChange={(e) =>
                        dispatch({
                          type: actionTypes.SET_MONTH_DAY,
                          payload: e.target.value,
                        })
                      }
                      defaultValue={initialState.monthDay}
                    >
                      {modalConstants.dayOfTheMonth.map((day) => (
                        <option key={`dayOfTheMonth-${day}`} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  )}
                  {state.reportPeriod === "weekly" && (
                    <select
                      className=" block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                      id="periodDay"
                      onChange={(e) =>
                        dispatch({
                          type: actionTypes.SET_WEEK_DAY,
                          payload: e.target.value,
                        })
                      }
                      defaultValue={initialState.weekDay}
                    >
                      {Object.keys(modalConstants.daysOfTheWeek).map((day) => (
                        <option
                          key={`daysOfTheWeek-${day}`}
                          value={modalConstants.daysOfTheWeek[day]}
                        >
                          {day}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              <div className="w-1/2 md:w-1/4 px-2 mb-3">
                <label
                  className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  htmlFor="periodHour"
                >
                  <Translation>
                    {(t) => capitalizeFirst(t("delivery time"))}
                  </Translation>
                </label>
                <select
                  className=" block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  id="periodHour"
                  onChange={(e) => {
                    dispatch({
                      type: actionTypes.SET_SELECTED_HOUR,
                      payload: e.target.value,
                    });
                  }}
                  defaultValue={initialState.selectedHour}
                >
                  {Object.keys(modalConstants.hoursInDay).map((hour) => (
                    <option
                      key={`hour-${hour}`}
                      value={modalConstants.hoursInDay[hour]}
                    >
                      {hour}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-full md:w-1/4 px-2 mb-3">
                <label
                  className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  htmlFor="timezone"
                >
                  <Translation>
                    {(t) => capitalizeFirst(t("timezone"))}
                  </Translation>
                </label>
                <select
                  className=" block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  id="timezone"
                  onChange={(e) => {
                    dispatch({
                      type: actionTypes.SET_TIMEZONE,
                      payload: e.target.value,
                    });
                  }}
                  defaultValue={initialState.timezone}
                >
                  {Object.keys(modalConstants.utcTimeZones).map((timeZone) => (
                    <option
                      key={`timeZone-${timeZone}`}
                      value={modalConstants.utcTimeZones[timeZone]}
                    >
                      {timeZone}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-wrap -mx-3">
              {state.selectedSite?.breakdowns?.includes("statType") && (
                <div className="w-full md:w-1/4 px-2 mb-3">
                  <label
                    className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    htmlFor="incomeTypes"
                  >
                    <Translation>
                      {(t) => capitalizeFirst(t("income type"))}
                    </Translation>
                  </label>
                  {state.selectedSite?.incomeTypes && (
                    <MultiSelectMenu
                      fullWidth
                      data={state.selectedSite.incomeTypes}
                      title="income type"
                      selectedParam={selectedIncomeTypes}
                      setSelectedParam={setSelectedIncomeTypes}
                    />
                  )}
                </div>
              )}
              <div className="w-full md:w-1/4 px-2 mb-3">
                <label
                  className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  htmlFor="reportBD"
                >
                  Site
                </label>
                <select
                  className=" block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  id="selected-site"
                  onChange={(e) => {
                    const selectedSite = sites.find(
                      (s) => s.id === e.target.value
                    );
                    dispatch({
                      type: actionTypes.SET_SELECTED_SITE,
                      payload: selectedSite,
                    });
                  }}
                  defaultValue={initialState.selectedSite?.id}
                >
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.fqdn}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-full md:w-3/4 px-2 mb-3">
                <label
                  className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  htmlFor="reportBD"
                >
                  <Translation>
                    {(t) => capitalizeFirst(t("filters"))}
                  </Translation>
                </label>
                <div className="flex flex-row">
                  <MultiselectBox
                    title={capitalize(t("ad placement"))}
                    options={
                      state.selectedSite?.zones?.map((a) => ({
                        id: a.id,
                        label: a.name + " (" + a.id + ")",
                      })) ?? []
                    }
                    onChange={onAdPlacementChange}
                    onConfig={true}
                    customStyle={{
                      maxHeight: "250px",
                      maxWidth: "400px",
                    }}
                    customClass="overflow-x-scroll"
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap -mx-3">
              <div className=" px-2 mb-3">
                <label
                  className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  htmlFor="reportBD"
                >
                  <Translation>
                    {(t) => capitalizeFirst(t("breakdowns"))}
                  </Translation>
                </label>
                <span
                  className="md:inline-block bg-gray-200 text-gray-700 border border-gray-200 rounded py-2 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  id="reportBD"
                >
                  {state.siteBreakdowns.map((bd) => (
                    <button
                      key={`breakdown-${bd}`}
                      className={`transition-all ease-out transition-fast text-xs font-bold px-2 py-2 rounded mr-2 cursor-pointer ${
                        state.selectedBreakdowns.indexOf(bd) !== -1
                          ? "bg-white shadow"
                          : ""
                      }`}
                      onClick={(e) => onBreakdownChange(e)}
                      data-breakdown={bd}
                    >
                      <label className="mr-1 float-left cursor-pointer">
                        {upperCase(t(modalConstants.siteBreakdownsAll[bd]))}
                      </label>
                    </button>
                  ))}
                </span>
              </div>
              <div className="px-2 mb-3">
                <label
                  className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  htmlFor="reportFilters"
                >
                  <Translation>
                    {(t) => capitalizeFirst(t("platforms"))}
                  </Translation>
                </label>

                <span className="md:inline-block bg-gray-200 text-gray-700 border border-gray-200 rounded py-2 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500">
                  {state.sitePlatforms.map((platform) => (
                    <button
                      key={`platform-${platform}`}
                      className={`transition-all ease-out transition-fast text-xs font-bold px-2 py-2 rounded mr-2 cursor-pointer  ${
                        state.selectedPlatforms.indexOf(platform) !== -1
                          ? "bg-white shadow"
                          : ""
                      }`}
                      onClick={(e) => onPlatformChange(e)}
                      data-platform={platform}
                    >
                      {upperCase(
                        t(modalConstants.sitePlatformsAll?.[platform])
                      )}
                    </button>
                  ))}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap -mx-3">
              <div className="w-full px-2 ">
                <label
                  className={`${
                    !isEmailEmpty ? "text-gray-700" : "text-red-400"
                  } block uppercase tracking-wide text-xs font-bold mb-2`}
                  htmlFor="grid-password"
                >
                  <Translation>
                    {(t) => capitalizeFirst(t("recipients"))}
                  </Translation>
                </label>

                <TagsInput
                  tags={state?.emails}
                  setTags={(emails) => {
                    dispatch({ type: actionTypes.SET_EMAILS, payload: emails });
                  }}
                  isTagsEmpty={isEmailEmpty}
                  setIsTagsEmpty={setIsEmailEmpty}
                />
              </div>
            </div>
          </div>
          <div className="p-3 flex items-center float-right">
            <div>
              <button
                className="text-sm text-white bg-empower-500 rounded-md px-4 py-2 mx-2"
                onClick={handleAdd}
              >
                {operation === "update"
                  ? capitalize(t("update"))
                  : capitalize(t("create"))}
              </button>
              <button
                className="text-sm text-white border rounded-md px-4 py-2 bg-gray-500"
                onClick={onClose}
              >
                <Translation>{(t) => capitalizeFirst(t("cancel"))}</Translation>
              </button>
            </div>
          </div>
        </>
      </div>
    </div>
  );
};

export default ConfigModal;
